import {
  collection,
  query,
  where,
  orderBy,
  startAfter,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  QueryDocumentSnapshot,
  DocumentData,
  limit,
} from 'firebase/firestore';
import { getDb } from './firebase';
import { generateQueryKeywords } from '@2bforest/common/utils';
import type {
  ProductPricing,
  ProductType,
  PricingCategory1,
  PricingCategory2,
  PricingCategory3,
  PricingCategory4,
} from '../types';
import type { PricingImportData } from '../utils/excelImport';

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

export interface ListResult<T> {
  items: T[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// ProductPricing — full CRUD
// ---------------------------------------------------------------------------

const PRODUCT_PRICING = 'product_pricing';

function toProductPricing(snap: QueryDocumentSnapshot<DocumentData>): ProductPricing {
  const d = snap.data();
  return {
    id: snap.id,
    productType: d.productType ?? '',
    productTypeId: d.productTypeId ?? '',
    category1: d.category1,
    category1Id: d.category1Id,
    category2: d.category2,
    category2Id: d.category2Id,
    category3: d.category3,
    category3Id: d.category3Id,
    category4: d.category4,
    category4Id: d.category4Id,
    designation: d.designation ?? '',
    price: d.price ?? 0,
    vat: d.vat ?? 0,
    notes: d.notes,
    applicableLaw: d.applicableLaw,
    searchKeywords: d.searchKeywords ?? [],
  };
}

export interface ListProductPricingParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
  searchQuery?: string;
  productTypeId?: string;
  category1Id?: string;
  category2Id?: string;
  category3Id?: string;
  category4Id?: string;
}

export async function listProductPricing({
  pageSize = 50,
  lastDoc: cursor = null,
  searchQuery = '',
  productTypeId = '',
  category1Id = '',
  category2Id = '',
  category3Id = '',
  category4Id = '',
}: ListProductPricingParams = {}): Promise<ListResult<ProductPricing>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const constraints: any[] = [];

  if (searchQuery) {
    constraints.push(
      where('searchKeywords', 'array-contains-any', generateQueryKeywords(searchQuery))
    );
  } else if (productTypeId) {
    constraints.push(where('productTypeId', '==', productTypeId));
  }

  if (category1Id) constraints.push(where('category1Id', '==', category1Id));
  if (category2Id) constraints.push(where('category2Id', '==', category2Id));
  if (category3Id) constraints.push(where('category3Id', '==', category3Id));
  if (category4Id) constraints.push(where('category4Id', '==', category4Id));

  constraints.push(orderBy('designation', 'asc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize + 1));

  const snapshot = await getDocs(query(collection(getDb(), PRODUCT_PRICING), ...constraints));
  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;

  return {
    items: pageDocs.map(toProductPricing),
    lastDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    hasMore,
  };
}

export async function createProductPricing(data: Omit<ProductPricing, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), PRODUCT_PRICING), data);
  return ref.id;
}

export async function updateProductPricing(
  id: string,
  data: Partial<Omit<ProductPricing, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), PRODUCT_PRICING, id), data);
}

export async function deleteProductPricing(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), PRODUCT_PRICING, id));
}

// ---------------------------------------------------------------------------
// Product Types
// ---------------------------------------------------------------------------

const PRODUCT_TYPES = 'product_types';

function toProductType(snap: QueryDocumentSnapshot<DocumentData>): ProductType {
  const d = snap.data();
  return {
    id: snap.id,
    name: d.name ?? '',
    order: d.order ?? 0,
    linkedCategory1Ids: d.linkedCategory1Ids ?? [],
  };
}

export async function listProductTypes(): Promise<ProductType[]> {
  const snapshot = await getDocs(
    query(collection(getDb(), PRODUCT_TYPES), orderBy('order', 'asc'))
  );
  return snapshot.docs.map(toProductType);
}

export async function createProductType(data: Omit<ProductType, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), PRODUCT_TYPES), data);
  return ref.id;
}

export async function updateProductType(
  id: string,
  data: Partial<Omit<ProductType, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), PRODUCT_TYPES, id), data);
}

export async function deleteProductType(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), PRODUCT_TYPES, id));
}

// ---------------------------------------------------------------------------
// Pricing Category 1
// ---------------------------------------------------------------------------

const CATEGORY1 = 'pricing_category1';

function toCategory1(snap: QueryDocumentSnapshot<DocumentData>): PricingCategory1 {
  const d = snap.data();
  return {
    id: snap.id,
    name: d.name ?? '',
    order: d.order ?? 0,
    linkedCategory2Ids: d.linkedCategory2Ids ?? [],
  };
}

export async function listCategory1(): Promise<PricingCategory1[]> {
  const snapshot = await getDocs(query(collection(getDb(), CATEGORY1), orderBy('order', 'asc')));
  return snapshot.docs.map(toCategory1);
}

export async function createCategory1(data: Omit<PricingCategory1, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), CATEGORY1), data);
  return ref.id;
}

export async function updateCategory1(
  id: string,
  data: Partial<Omit<PricingCategory1, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), CATEGORY1, id), data);
}

export async function deleteCategory1(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), CATEGORY1, id));
}

// ---------------------------------------------------------------------------
// Pricing Category 2
// ---------------------------------------------------------------------------

const CATEGORY2 = 'pricing_category2';

function toCategory2(snap: QueryDocumentSnapshot<DocumentData>): PricingCategory2 {
  const d = snap.data();
  return {
    id: snap.id,
    name: d.name ?? '',
    order: d.order ?? 0,
    linkedCategory3Ids: d.linkedCategory3Ids ?? [],
  };
}

export async function listCategory2(): Promise<PricingCategory2[]> {
  const snapshot = await getDocs(query(collection(getDb(), CATEGORY2), orderBy('order', 'asc')));
  return snapshot.docs.map(toCategory2);
}

export async function createCategory2(data: Omit<PricingCategory2, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), CATEGORY2), data);
  return ref.id;
}

export async function updateCategory2(
  id: string,
  data: Partial<Omit<PricingCategory2, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), CATEGORY2, id), data);
}

export async function deleteCategory2(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), CATEGORY2, id));
}

// ---------------------------------------------------------------------------
// Pricing Category 3
// ---------------------------------------------------------------------------

const CATEGORY3 = 'pricing_category3';

function toCategory3(snap: QueryDocumentSnapshot<DocumentData>): PricingCategory3 {
  const d = snap.data();
  return {
    id: snap.id,
    name: d.name ?? '',
    order: d.order ?? 0,
    linkedCategory4Ids: d.linkedCategory4Ids ?? [],
  };
}

export async function listCategory3(): Promise<PricingCategory3[]> {
  const snapshot = await getDocs(query(collection(getDb(), CATEGORY3), orderBy('order', 'asc')));
  return snapshot.docs.map(toCategory3);
}

export async function createCategory3(data: Omit<PricingCategory3, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), CATEGORY3), data);
  return ref.id;
}

export async function updateCategory3(
  id: string,
  data: Partial<Omit<PricingCategory3, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), CATEGORY3, id), data);
}

export async function deleteCategory3(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), CATEGORY3, id));
}

// ---------------------------------------------------------------------------
// Pricing Category 4
// ---------------------------------------------------------------------------

const CATEGORY4 = 'pricing_category4';

function toCategory4(snap: QueryDocumentSnapshot<DocumentData>): PricingCategory4 {
  const d = snap.data();
  return { id: snap.id, name: d.name ?? '', order: d.order ?? 0 };
}

export async function listCategory4(): Promise<PricingCategory4[]> {
  const snapshot = await getDocs(query(collection(getDb(), CATEGORY4), orderBy('order', 'asc')));
  return snapshot.docs.map(toCategory4);
}

export async function createCategory4(data: Omit<PricingCategory4, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), CATEGORY4), data);
  return ref.id;
}

export async function updateCategory4(
  id: string,
  data: Partial<Omit<PricingCategory4, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), CATEGORY4, id), data);
}

export async function deleteCategory4(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), CATEGORY4, id));
}

// ---------------------------------------------------------------------------
// Excel import (replace-all)
// ---------------------------------------------------------------------------

const BATCH_MAX = 499;

async function clearFirestoreCollection(collectionName: string): Promise<void> {
  const snapshot = await getDocs(collection(getDb(), collectionName));
  for (let i = 0; i < snapshot.docs.length; i += BATCH_MAX) {
    const batch = writeBatch(getDb());
    snapshot.docs.slice(i, i + BATCH_MAX).forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
}

/**
 * Replace all product pricing data in Firestore with parsed Excel data.
 * Clears all 6 collections then batch-writes fresh data.
 */
export async function importProductPricingFromExcel(data: PricingImportData): Promise<void> {
  const allCollections = [
    PRODUCT_PRICING,
    PRODUCT_TYPES,
    CATEGORY1,
    CATEGORY2,
    CATEGORY3,
    CATEGORY4,
  ];
  await Promise.all(allCollections.map(clearFirestoreCollection));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const writeInBatches = async (collectionName: string, items: any[]) => {
    for (let i = 0; i < items.length; i += BATCH_MAX) {
      const batch = writeBatch(getDb());
      items.slice(i, i + BATCH_MAX).forEach(item => {
        batch.set(doc(getDb(), collectionName, item.id), item);
      });
      await batch.commit();
    }
  };

  await writeInBatches(PRODUCT_TYPES, data.productTypes);
  await writeInBatches(CATEGORY1, data.category1Items);
  await writeInBatches(CATEGORY2, data.category2Items);
  await writeInBatches(CATEGORY3, data.category3Items);
  await writeInBatches(CATEGORY4, data.category4Items);
  await writeInBatches(PRODUCT_PRICING, data.pricingItems);
}
